import { onScopeDispose, watch } from "vue";
import { useRoute, useRouter, type LocationQuery, type RouteLocationRaw } from "vue-router";
import { routePath, slugFromPath, useRouteLinksStore } from "../store/routeLinks";
import { useSearchSessionStore } from "../store/searchSession";
import { tabFromPath } from "../utils/navigation";
import { trackEvent } from "../analytics";
import { onSearchEntry } from "./searchHistoryEntry";

// Отметка в history.state записи, которую положили сюда (push) переходы к
// карточкам: depth - сколько таких записей лежит поверх исходной (без
// карточки), from - откуда сделан этот push, overSearch - стопка лежит поверх
// записи полноэкранного поиска (searchHistoryEntry.ts). replace её сохраняет.
// base - адрес записи под всей стопкой (без отметки): если там самой открыта
// карточка (маршрут, точка или профиль /user/<имя> - тап по линии на карте,
// прямая ссылка), крестик после go(-depth) уводит и с неё в покой.
const CARD_STATE = "ecoCard";
interface CardState { depth: number; from: string; overSearch?: boolean; base?: string }

// Публичный профиль автора (/user/<имя>) - панель поверх карты, как карточка маршрута.
export const PROFILE_PREFIX = "/user/";
export const profilePath = (username: string) => `${PROFILE_PREFIX}${encodeURIComponent(username)}`;
const isProfilePath = (path: string) => path.startsWith(PROFILE_PREFIX);
// Сколько ждать приземления go(-depth), прежде чем снять одноразовый слушатель.
const GO_LANDING_TIMEOUT_MS = 2000;

// Переходы к карточкам объектов (?route= / ?point=) и обратно (вызывается один
// раз, в App.vue).
// - Открытие из поиска - шаг в истории браузера (push): «назад» браузера снимает
//   ?route= / ?point=, и если объект открыли из поиска
//   (searchSession.markOpenedFromSearch(), его делает SearchPanel до emit),
//   поиск возвращается в том же виде - consumeReturn() в watch ниже.
// - «Назад» в карточке: точка поверх маршрута - снять только точку; ждёт возврат
//   в поиск - router.back() (и только он: поиск откроет тот же watch); иначе - как крестик.
// - Крестик: в покой; записи, положенные push'ем, снимаются go(-depth) (и
//   запись поиска под ними, если есть), а не replace - иначе в истории
//   остаётся мёртвая запись [/map, /map].
// - «Назад» браузера с записи полноэкранного поиска (searchHistoryEntry.ts) -
//   поиск закрыт (afterEach ниже).
export function useSearchNavigation() {
  const route = useRoute();
  const router = useRouter();
  const session = useSearchSessionStore();
  const links = useRouteLinksStore();

  // Открытый маршрут: код из /route/<slug> или (старая ссылка) ?route=<id>.
  const routeKey = (): string | null => {
    const slug = slugFromPath(route.path);
    if (slug) return slug;
    const raw = route.query.route;
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v ? String(v) : null;
  };
  // Путь вкладки карты, на котором идём дальше (карточка может лежать и на /map, и на /route/x).
  const mapPath = () => (tabFromPath(route.path) === "map" ? route.path : "/map");

  const cardState = (): CardState | null => {
    const v = (router.options.history.state as Record<string, unknown> | undefined)?.[CARD_STATE];
    return v && typeof v === "object" && typeof (v as CardState).depth === "number" ? (v as CardState) : null;
  };

  const pushCard = (loc: { path: string; query: LocationQuery }) => {
    const c = cardState();
    const overSearch = c ? !!c.overSearch : onSearchEntry(router);
    const base = c ? c.base : route.fullPath;
    return router.push({
      ...loc,
      state: { [CARD_STATE]: { depth: (c?.depth ?? 0) + 1, from: route.fullPath, overSearch, base } },
    });
  };

  // replace с сохранением отметки (memory-история vue-router её сама не переносит).
  const replaceLoc = (loc: { path: string; query: LocationQuery }) => {
    const c = cardState();
    return router.replace({ ...loc, ...(c ? { state: { [CARD_STATE]: { ...c } } } : {}) });
  };
  const replaceQuery = (query: LocationQuery) => replaceLoc({ path: route.path, query });

  // Адрес карточки маршрута: /route/<код> (код - из списков или запросом); если
  // кода нет (маршрут недоступен) - прежний /map?route=<id>. Маршрут не
  // открывается под карточкой точки - point снимается.
  const routeLoc = async (routeId: number): Promise<{ path: string; query: LocationQuery }> => {
    const q: LocationQuery = { ...route.query };
    delete q.route;
    delete q.point;
    const slug = await links.ensureSlug(routeId);
    return slug ? { path: routePath(slug), query: q } : { path: "/map", query: { ...q, route: String(routeId) } };
  };

  // Из поиска.
  const openRoute = async (routeId: number) => {
    trackEvent("Route: View Card");
    return pushCard(await routeLoc(routeId));
  };
  const openPoint = (id: string) => pushCard({ path: mapPath(), query: { ...route.query, point: id } });

  // Профиль автора (ссылка «Автор» в карточке маршрута) - шаг истории поверх
  // карточки: «Назад» профиля возвращает к ней. Маршрут из списка профиля -
  // openRoute, его «Назад» возвращает в профиль (back() ниже).
  const openProfile = (username: string) => pushCard({ path: profilePath(username), query: {} });

  // «Назад» панели профиля: предыдущая запись истории внутри приложения, если
  // она есть - профиль открыт переходом (отметка ecoCard) или web-история
  // vue-router знает запись позади (history.state.back); иначе (прямая ссылка) - в покой.
  const backFromProfile = () => {
    const prev = (router.options.history.state as Record<string, unknown> | undefined)?.back;
    if (cardState() || prev) {
      router.back();
      return;
    }
    close();
  };

  // Из карточки («Точки рядом»): шаг истории, только если карточка сама из
  // цепочки поиска; иначе - на месте, «Назад» по-прежнему ведёт в покой.
  const openFromCard = async (target: { route: number } | { point: string }) => {
    if ("route" in target) trackEvent("Route: View Card");
    const loc = "route" in target ? await routeLoc(target.route) : { path: route.path, query: { ...route.query, point: target.point } };
    return cardState() ? pushCard(loc) : replaceLoc(loc);
  };

  // Тап по линии на карте: не из поиска - ожидающий возврат в поиск снимается.
  const selectRouteFromMap = async (routeId: number | null) => {
    if (session.returnTo) session.cancel();
    // Поверх профиля - шаг истории: «Назад» маршрута возвращает в профиль.
    if (routeId != null) {
      const loc = await routeLoc(routeId);
      return isProfilePath(route.path) ? pushCard(loc) : replaceLoc(loc);
    }
    const q = { ...route.query };
    delete q.route;
    return replaceLoc({ path: "/map", query: q });
  };

  // Старая ссылка /map?route=<id>: заменяем на /route/<код> (replace, без шага истории).
  const normalizeLegacyRoute = async () => {
    if (slugFromPath(route.path)) return;
    const raw = route.query.route;
    const id = Number(Array.isArray(raw) ? raw[0] : raw);
    if (!Number.isFinite(id) || id <= 0) return;
    const slug = await links.ensureSlug(id);
    if (!slug || slugFromPath(route.path)) return;
    const q = { ...route.query };
    delete q.route;
    await replaceLoc({ path: routePath(slug), query: q });
  };

  // Тап по точке на карте: не из поиска, на месте (replace). Поверх карточки
  // маршрута - стопкой (её «Назад» снимет только точку, маршрут сохраняет свой
  // возврат в поиск); без маршрута ожидающий возврат в поиск снимается, как у
  // тапа по линии.
  const openPointFromMap = (id: string) => {
    if (route.query.point === id) return;
    if (!routeKey() && session.returnTo) session.cancel();
    return replaceQuery({ ...route.query, point: id });
  };

  const isCardLocation = (fullPath: string) => {
    const loc = router.resolve(fullPath);
    return !!(slugFromPath(loc.path) || loc.query.route || loc.query.point || isProfilePath(loc.path));
  };

  const close = () => {
    session.cancel();
    const c = cardState();
    const depth = c ? c.depth + (c.overSearch ? 1 : 0) : 0;
    if (depth > 0) {
      // Под стопкой - тоже карточка: вернувшись к ней, уходим в покой.
      if (c?.base && isCardLocation(c.base)) {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const stop = router.afterEach((_to, _from, failure) => {
          stop();
          if (timer) clearTimeout(timer);
          if (!failure) void router.replace("/map");
        });
        // go() без навигации (нет записи позади) - слушатель не должен утечь.
        timer = setTimeout(stop, GO_LANDING_TIMEOUT_MS);
      }
      router.go(-depth);
      return;
    }
    const q = { ...route.query };
    delete q.route;
    delete q.point;
    void router.replace({ path: "/map", query: q });
  };

  const back = () => {
    const q = { ...route.query };
    if (q.point && (routeKey() || isProfilePath(route.path))) {
      delete q.point;
      const target = router.resolve({ path: route.path, query: q }).fullPath;
      if (cardState()?.from === target) router.back();
      else void replaceQuery(q);
      return;
    }
    if (session.returnTo === "search") {
      router.back();
      return;
    }
    // Карточка открыта из профиля автора - «Назад» возвращает в профиль.
    const from = cardState()?.from;
    if (from && isProfilePath(from)) {
      router.back();
      return;
    }
    close();
  };

  // Уход с записи полноэкранного поиска на запись без отметки («назад»
  // браузера) при открытом поиске - поиск закрыт. Остальные уходы с неё
  // (карточка, «Отмена», каталог) к этому моменту уже закрыли сессию сами.
  let wasOnSearchEntry = onSearchEntry(router);
  const stopAfterEach = router.afterEach((_to, _from, failure) => {
    if (failure) return;
    const now = onSearchEntry(router);
    if (wasOnSearchEntry && !now && session.open) session.cancel();
    wasOnSearchEntry = now;
  });
  onScopeDispose(stopAfterEach);

  watch(
    () => [route.path, routeKey(), route.query.point] as const,
    ([path, routeId, pointId], [prevPath, prevRouteId, prevPointId]) => {
      const onMap = tabFromPath(path) === "map";
      const wasOnMap = tabFromPath(prevPath) === "map";
      // Уход с карты - выход из поиска: ожидающий возврат и текст не переживают его.
      if (wasOnMap && !onMap) {
        if (session.open || session.returnTo || session.query) session.cancel();
        return;
      }
      const cardWasOpen = !!(prevRouteId || prevPointId);
      const cardOpen = !!(routeId || pointId);
      if (onMap && wasOnMap && cardWasOpen && !cardOpen) session.consumeReturn();
    }
  );

  return { openRoute, openPoint, openProfile, backFromProfile, openFromCard, selectRouteFromMap, normalizeLegacyRoute, openPointFromMap, replaceQuery, back, close };
}
